import { ChangeEvent, FocusEvent, useEffect, useState } from 'react';
import { memo, useRef } from 'react';
import { useKeyboardIntegration } from '../../../../context/Keyboard/keyboard';
import { useFormikContext, useField } from 'formik';
import { BaseTextField } from '../../BaseTextField/BaseTextField';
import { useKioskState } from '../../../../context/Kiosk/kiosk';
import { useLocale } from '../../../../context/Localization/localization';
import { GetValueReturn, OnInputChangeProps, OnInputChangeReturn, SetValueProps } from '../../../../context/Keyboard/keyboard.types';
import { KioskFormTextFieldProps } from '../FormTextField.types';
import { TextFieldValidation } from '../../../../helpers/FormFieldValidation';
import { useScrollToOnError } from '../../../../helpers/useScrollTo';

export const KioskFormTextField = memo(
    ({
        name,
        id,
        title,
        label,
        ariaLabel,
        disabled,
        required,
        autoFocus,
        keyboardLayout,
        shiftAfterSpace,
        shiftAfterPeriod,
        shiftAfterAmpersand,
        hideKeyboardOnClickOut,
        capsLock,
        fullWidth,
        seventyFivePercentWidth,
        tabFocusRefContext,
        onChangeCallback,
        onBlurCallback,
    }: KioskFormTextFieldProps) => {
        /**
         * Not all formik validation accounts for TextFields that are null.
         * So even though we pass an undefined validation type to the function,
         * it is still required in order to have null required fields validate.
         */
        const [field, meta] = useField<string>({
            name,
            validate: TextFieldValidation({
                required: required ?? false,
                label,
                name,
                validationType: undefined,
            }),
        });
        const { submitCount, setFieldValue } = useFormikContext();

        /**
         * A ref to the HTMLInputElement in the MUI TextField.
         */
        const inputRef = useRef<HTMLInputElement>(null);
        /**
         * The focus ref to prevent a stale state when the on-screen keyboard
         * calls any functions that need to know if the field is focused or not.
         */
        const isFocused = useRef<boolean>(false);
        /**
         * The input value ref to prevent a stale state when the on-screen keyboard
         * calls any functions that use the internal value state.
         */
        const valueRef = useRef<string>(field.value ?? '');
        /**
         * The caret position ref to prevent a stale state when the on-screen keyboard
         * calls any functions that use the internal caret position state.
         */
        const caretPositionRef = useRef<number | null>(null);
        /**
         * Boolean state to update the MUI TextField label's position since it does not always
         * float up when the field has a value.
         */
        const [shrink, setShrink] = useState<boolean>(false);
        /**
         * Boolean state to disable the label animation for floating up when the field has a value initially.
         * This way the label appears to already be set.
         */
        const [disableAnimation, setDisableAnimation] = useState<boolean>(true);
        useScrollToOnError({
            scrollable: inputRef,
            name: name,
        });

        const locale = useLocale();
        const {
            dealer: { primaryLanguage },
        } = useKioskState();

        useEffect(() => {
            if (tabFocusRefContext) {
                tabFocusRefContext.current = inputRef.current;
            }
        }, [tabFocusRefContext]);

        useEffect(() => {
            /**
             * When formik value is different than our internal value, then we go ahead and synchronize.
             */
            if (field.value !== valueRef.current && field.value !== null) {
                valueRef.current = field.value;
            }

            /**
             * If the value being passed in has an actual value and the label is not already
             * in the focused position, then we go ahead and set shrink to true so that it floats
             * up.
             */
            if (field.value !== null && field.value.length > 0 && !shrink) {
                setShrink(true);
            }

            /**
             * 1 second timeout for the disabled animation on initial render was about correct
             * for the label to appear automatically in the correct position.
             */
            setTimeout(() => {
                setDisableAnimation(false);
            }, 1);
        }, [field.value, shrink]);

        useEffect(() => {
            /**
             * Updates the internal caret position when the internal MUI value finishes changing from the
             * pattern mask.
             */
            if (inputRef.current) {
                inputRef.current.setSelectionRange(caretPositionRef.current, caretPositionRef.current);
            }
        }, [inputRef.current?.value]);

        /**
         * Used by the keyboard context and is described on the declaration there.
         */
        const getValue = (): GetValueReturn => {
            if (inputRef.current && inputRef.current.selectionStart !== null && inputRef.current.selectionEnd !== null) {
                const selectionStart: number = inputRef.current.selectionStart;
                const selectionEnd: number = inputRef.current.selectionEnd;

                return {
                    inputValue: valueRef.current,
                    caretPositionStart: selectionStart,
                    caretPositionEnd: selectionEnd,
                };
            }
            else {
                return {
                    inputValue: valueRef.current,
                    caretPositionStart: valueRef.current.length,
                    caretPositionEnd: valueRef.current.length,
                };
            }
        };

        /**
         * Used by the keyboard context and is described on the declaration there.
         */
        const setValue = ({ value, caretPosition }: SetValueProps): void => {
            caretPositionRef.current = caretPosition;
            onChangeCallback && onChangeCallback();
            valueRef.current = value;
            setFieldValue(name, value);
        };

        /**
         * Used by the keyboard context and is described on the declaration there.
         */
        const onInputChange = ({ value, caretPosition, wasBackspace, wasSelectionRange }: OnInputChangeProps): OnInputChangeReturn => {
            return {
                value: value,
                caretPosition: caretPosition,
            };
        };

        /**
         * Used by the keyboard context and is described on the declaration there.
         */
        const onKeyboardInput = (): void => {
            if (inputRef.current && !isFocused.current) {
                inputRef.current.focus();
            }
        };

        const { onFocus, onBlur } = useKeyboardIntegration({
            fieldValue: field.value,
            getValue: getValue,
            setValue: setValue,
            onInputChange: onInputChange,
            onKeyboardInput: onKeyboardInput,
            keyboardLayout: keyboardLayout,
            shiftAfterPeriod: shiftAfterPeriod,
            shiftAfterAmpersand: shiftAfterAmpersand,
            shiftAfterSpace: shiftAfterSpace,
            hideKeyboardOnClickOut: hideKeyboardOnClickOut,
            capsLock: capsLock,
        });

        const handleOnBlur = () => {
            onBlur();
            onBlurCallback && onBlurCallback();

            /**
             * Sets shrink to false so that the label floats down if there is no value in the field.
             */
            if (valueRef.current.length === 0 || valueRef.current === null) {
                setShrink(false);
            }
            isFocused.current = false;
        };

        const handleOnChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            caretPositionRef.current = event.target.selectionEnd;
            onChangeCallback && onChangeCallback();
            valueRef.current = event.target.value;
            setFieldValue(name, event.target.value);
        };

        const handleOnFocus = (event: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            /**
             * If shrink is not true, when we focuse we go ahead and set it to true.
             */
            if (!shrink) {
                setShrink(true);
            }
            isFocused.current = true;
            onFocus(event);
        };

        return (
            <BaseTextField
                name={name}
                id={id}
                title={title}
                label={label}
                ariaLabel={ariaLabel}
                inputMode={'text'}
                type={'text'}
                inputRef={inputRef}
                value={field.value ?? ''}
                error={meta.error}
                touched={meta.touched || submitCount > 0}
                disabled={disabled}
                required={required}
                autoFocus={autoFocus}
                fullWidth={fullWidth}
                seventyFivePercentWidth={seventyFivePercentWidth}
                capsLock={capsLock}
                onFocus={handleOnFocus}
                onChange={handleOnChange}
                onBlur={handleOnBlur}
                InputLabelProps={{
                    shrink: shrink,
                    style: {
                        transition: disableAnimation ? 'none' : undefined,
                    },
                }}
                safariHack={locale === primaryLanguage ? 'primarylang' : 'alternatelang'}
            />
        );
    },
);
